// PackagingType.tsx (full file with the addition)

import { useState, useEffect, useRef } from 'react';
import { Axios, getErrorMessage } from '@helpers/ApiService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch,
  faSquareCheck,
  faSquareXmark,
  faCheck,
} from '@fortawesome/free-solid-svg-icons';
import { Assets } from '@helpers/Constants/Assets';
import {
  errorMessage,
  formRegex,
  onboardingStepId,
  pagination,
  productSubsteps,
  responseTexts,
  toastMessage,
} from '@helpers/Constants';
import { useToast } from '@contexts/Toast';
import {
  FeetypeProps,
  MultiSelectionProps,
  PackagingTypes,
  ServiceRateFeetypeSelection,
  SizeData,
  ApiPackagingTypeResponse,
} from '@helpers/Models';
import { ButcherHeader } from '@components/ButcherHeader';
import { useLocation, useNavigate } from 'react-router-dom';
import { ApiEndpoints, FrontendRoutes } from '@helpers/Constants/Navigation';
import { Breadcrumbs } from '@components/Breadcrumbs';
import { faTrashCan } from '@fortawesome/free-regular-svg-icons';
import Pagination from '@components/Pagination';
import { UseConfirmationToast } from '@contexts/ConfirmationToast';
import { PackageFeeTypeBottomSheet } from '@components/BottomSheet/PackageFeeTypeBottomSheet';
import { OnBoardSettings } from '@components/OnBoardSettings';
import { useLocalStorage } from '@hooks/useLocalStorage';
import { useUnsavedChangesPrompt } from '@hooks/useNavigationBlocker';
import { isModified } from '@services/isModified';

export const PackagingType: React.FC = () => {
  const location = useLocation();
  const fromSettings = location.state?.fromSettings;
  const { updateOnboardingStep } = useLocalStorage();
  const [packagingTypes, setPackagingTypes] = useState<PackagingTypes[]>([]);
  const [deletedPackageTypes, setDeletedPackageTypes] = useState<PackagingTypes[]>([]);
  const [initialList, setInitialList] = useState<PackagingTypes[]>([]);
  const { showToastMessage } = useToast();
  const { showConfirmationToast } = UseConfirmationToast();
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const navigate = useNavigate();
  const [selectAll, setSelectAll] = useState<boolean>(false);
  const [feeTypeOptions, setFeeTypeOptions] = useState<FeetypeProps[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(pagination.itemsPerPage);
  const [totalPages, setTotalPages] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [searchKey, setSearchKey] = useState('');
  const [selectedRecords, setSelectedRecords] = useState<MultiSelectionProps[]>([]);
  const [onDeletion, setOnDeletion] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isFeetypeSheetOpen, setisFeetypeSheetOpen] = useState(false);
  const [selectedFeeTypeRowIndex, setSelectedFeeTypeRowIndex] = useState<number | null>(null);

  const [sizeData, setSizeData] = useState<SizeData[]>([]);
  const [newsizeData, setNewsizeData] = useState('');
  const [addingSizeForRowIndex, setAddingSizeForRowIndex] = useState<number | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [tempIdCounter, setTempIdCounter] = useState(-1);

  // Compute used fee type IDs (for preventing deletion of in-use fee types)
  const usedFeeTypeIds = packagingTypes
    .filter(item => item.feeTypeId && item.feeTypeId > 0)
    .map(item => item.feeTypeId)
    .filter((id, index, self) => self.indexOf(id) === index);

  // Track previous sheet state to refresh fee types when it closes
  const prevIsFeetypeSheetOpen = useRef(isFeetypeSheetOpen);

  // --- Derive hasUnsavedChanges from comparison ---
  useEffect(() => {
    setHasUnsavedChanges(isModified(initialList, packagingTypes));
  }, [packagingTypes, initialList]);

  useUnsavedChangesPrompt(hasUnsavedChanges, undefined, 'Leave Without Saving');

  useEffect(() => {
    if (searchKey !== '') {
      setCurrentPage(1);
    }
    fetchAllPackageTypes(currentPage, searchKey, itemsPerPage);
  }, [currentPage, itemsPerPage, searchKey]);

  useEffect(() => {
    fetchSizes();
  }, []);

  // Fetch fee types when sheet opens, and refresh when it closes (to pick up restored fee types)
  useEffect(() => {
    fetchFeeType();
  }, [isFeetypeSheetOpen]);

  // Also refresh fee types when the bottom sheet closes
  useEffect(() => {
    if (prevIsFeetypeSheetOpen.current && !isFeetypeSheetOpen) {
      fetchFeeType(); // re-fetch to get any restored fee types
    }
    prevIsFeetypeSheetOpen.current = isFeetypeSheetOpen;
  }, [isFeetypeSheetOpen]);

  const fetchSizes = async () => {
    try {
      const response = await Axios.post(ApiEndpoints.FETCH_PACKAGE_SIZE, {});
      setSizeData(
        response.data.data.packageSize.map((sizes: SizeData) => ({
          id: sizes.id,
          packageSize: sizes.packageSize,
          packageSizeUnit: sizes.packageSizeUnit,
        })),
      );
    } catch (error) {
      // silently fail
    }
  };

  const fetchAllPackageTypes = async (
    page: number,
    searchKey: string,
    count: number = itemsPerPage,
  ): Promise<void> => {
    try {
      const response = await Axios.post(ApiEndpoints.FETCH_PACKAGE_TYPE, {
        page,
        count,
        searchKey,
      });

      const allItems = response.data.data.packageType.map(
        (item: ApiPackagingTypeResponse) => ({
          id: item.id,
          packageType: item.packageType,
          feeTypeId: item.feeTypeId,
          availableFlag: item.defaultFlag === 1 ? true : Boolean(item.availableFlag),
          price: item.price,
          defaultFlag: item.defaultFlag,
          packageSizeId: item.packageSizeId,
          deletedAt: item.deletedAt,
        }),
      );

      const active = allItems.filter((item: any) => !item.deletedAt);
      const deleted = allItems.filter((item: any) => item.deletedAt);

      setPackagingTypes(active);
      setInitialList(active);
      setDeletedPackageTypes(deleted);
      setCurrentPage(response.data.data.currentPage || 1);
      setTotalPages(response.data.data.totalPages || 1);
    } catch (error) {
      // silently fail
    }
  };

  const fetchFeeType = async (): Promise<void> => {
    try {
      const response = await Axios.post(ApiEndpoints.FETCH_PACKAGE_FEETYPE, {});
      setFeeTypeOptions(response.data.data.feeTypes);
    } catch (error) {
      // silently fail
    }
  };

  // ---- Check if there's an incomplete new row ----
  const hasIncompleteRow = (): boolean => {
    return packagingTypes.some((item) => {
      if (item.id !== undefined) return false; // saved row
      const nameEmpty = !item.packageType || item.packageType.trim() === '';
      const sizeMissing = !item.packageSizeId || item.packageSizeId === 0;
      const feeMissing = !item.feeTypeId || item.feeTypeId === 0;
      const priceEmpty = !item.price || Number(item.price) <= 0;
      return nameEmpty || sizeMissing || feeMissing || priceEmpty;
    });
  };

  // ---- Add More: insert a new empty row ----
  const handleAddMore = () => {
    if (hasIncompleteRow()) {
      showToastMessage(
        'Please complete the current packaging type before adding another.',
        'error'
      );
      return;
    }

    const newTempId = tempIdCounter;
    setTempIdCounter((prev) => prev - 1);

    const newItem: PackagingTypes = {
      packageType: '',
      price: '',
      availableFlag: true,
      defaultFlag: 0,
      packageSizeId: 0,
      feeTypeId: 0,
    };
    (newItem as any)._tempId = newTempId;

    setPackagingTypes([...packagingTypes, newItem]);
    // Scroll to bottom after render
    setTimeout(() => {
      const scrollArea = document.getElementById('packaging-table-scroll-area');
      if (scrollArea) {
        scrollArea.scrollTop = scrollArea.scrollHeight;
      }
    }, 100);
  };

  // ---- Validation ----
  const validateField = (name: string, value: string | number): string => {
    const stringData = typeof value === 'string' ? value.trim() : String(value);
    const numericData = Number(value);

    if (!stringData || (typeof value === 'number' && numericData === 0)) {
      return errorMessage.FIELD_REQUIRED;
    }

    switch (name) {
      case 'price':
        return !formRegex.PRICE.test(stringData)
          ? errorMessage.PRICE_ERROR
          : '';
      case 'packageType':
        return !formRegex.ALPHANUMERIC.test(stringData)
          ? errorMessage.ALPHANUMERIC_ERROR
          : '';
      default:
        return '';
    }
  };

  // ---- Validate all packaging types (skip default items) ----
  const validateAllPackages = (): boolean => {
    let isValid = true;
    const newErrors: { [key: string]: string } = {};

    packagingTypes.forEach((item, index) => {
      // Skip validation for default items (read-only)
      if (item.defaultFlag === 1) return;

      const err: { [key: string]: string } = {};

      // Package Type
      const packageTypeError = validateField('packageType', item.packageType);
      if (packageTypeError) {
        err.packageType = packageTypeError;
        isValid = false;
      }

      // Price
      const priceError = validateField('price', item.price);
      if (priceError) {
        err.price = priceError;
        isValid = false;
      }

      // Size (must be selected)
      if (!item.packageSizeId || item.packageSizeId === 0) {
        err.packageSizeId = errorMessage.FIELD_REQUIRED;
        isValid = false;
      }

      // Fee Type (must be selected)
      if (!item.feeTypeId || item.feeTypeId === 0) {
        err.feeTypeId = errorMessage.FIELD_REQUIRED;
        isValid = false;
      }

      // Store errors for this row
      if (Object.keys(err).length > 0) {
        for (const [field, msg] of Object.entries(err)) {
          newErrors[`${field}_${index}`] = msg;
        }
      }
    });

    setErrors((prev) => ({ ...prev, ...newErrors }));
    return isValid;
  };

  // ---- Row field changes ----
  const handleRowFieldChange = (
    index: number,
    field: string,
    value: any,
  ): void => {
    // Clear error for this specific field when it changes
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[`${field}_${index}`];
      return newErrors;
    });

    const updated = [...packagingTypes];
    updated[index] = {
      ...updated[index],
      [field]: field === 'packageSizeId' || field === 'feeTypeId' ? Number(value) : value,
    };
    setPackagingTypes(updated);
  };

  const handleSelectFeeTypeForRow = (feeType: ServiceRateFeetypeSelection) => {
    setisFeetypeSheetOpen(false);
    if (selectedFeeTypeRowIndex !== null) {
      const updated = [...packagingTypes];
      updated[selectedFeeTypeRowIndex] = {
        ...updated[selectedFeeTypeRowIndex],
        feeTypeId: feeType.id,
      };
      setPackagingTypes(updated);
      // Clear the fee type error for this row
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[`feeTypeId_${selectedFeeTypeRowIndex}`];
        return newErrors;
      });
      setSelectedFeeTypeRowIndex(null);
    }
  };

  const handleAvailableFlagChange = (index: number, value: boolean): void => {
    const updated = [...packagingTypes];
    updated[index] = {
      ...updated[index],
      availableFlag: value,
    };
    setPackagingTypes(updated);
  };

  const handleSelectAllChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const isChecked = e.target.checked;
    setSelectAll(isChecked);
    setPackagingTypes((prev) =>
      prev.map((item) => {
        if (item.defaultFlag === 1) {
          return { ...item, availableFlag: true };
        }
        return { ...item, availableFlag: isChecked };
      })
    );
  };

  // ---- Add new size from dropdown ----
  const handleAddNewSize = async (rowIndex: number | null) => {
    if (!newsizeData.trim()) return;
    if (!formRegex.PRICE.test(newsizeData)) {
      showToastMessage(errorMessage.INVALID_PACKAGE_SIZE, 'error');
      return;
    }
    const newSizeValue = Number(newsizeData);
    const isDuplicate = sizeData.some(
      (size) => size.packageSize === newSizeValue,
    );
    if (isDuplicate) {
      showToastMessage(errorMessage.DUPLICATE_SIZE_ERROR, 'error');
      return;
    }

    try {
      const response = await Axios.post(ApiEndpoints.CREATE_PACKAGE_SIZE, [
        { packageSize: newSizeValue },
      ]);
      await fetchSizes();
      const newSize = response.data?.data?.[0] || response.data?.[0];
      const newSizeId = newSize?.id;
      if (newSizeId) {
        if (rowIndex !== null && rowIndex >= 0) {
          const updated = [...packagingTypes];
          updated[rowIndex] = {
            ...updated[rowIndex],
            packageSizeId: newSizeId,
          };
          setPackagingTypes(updated);
          // Clear the packageSizeId error for this row
          setErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors[`packageSizeId_${rowIndex}`];
            return newErrors;
          });
        }
        setAddingSizeForRowIndex(null);
        setNewsizeData('');
        showToastMessage(responseTexts.PACKAGE_SIZE_CREATION_SUCCESS, 'success');
      } else {
        showToastMessage('New size added. Please select it from the dropdown.', 'success');
        setAddingSizeForRowIndex(null);
        setNewsizeData('');
      }
    } catch (error) {
      showToastMessage(errorMessage.MINTYPE_ADD_FAILURE, 'error');
    } finally {
      setAddingSizeForRowIndex(null);
      setNewsizeData('');
    }
  };

  // ---- Delete (handles both saved and unsaved) ----
  const performDelete = async (
    id: number | undefined,
    index: number,
  ): Promise<void> => {
    try {
      if (id !== undefined) {
        await Axios.delete(ApiEndpoints.DELETE_PACKAGE_TYPE, {
          data: { ids: [id] },
        });
        setPackagingTypes((prev) => prev.filter((item) => item.id !== id));
        showToastMessage(responseTexts.PACKAGE_TYPE_DELETE_SUCCESS, 'success');
      } else {
        setPackagingTypes((prev) => prev.filter((_, i) => i !== index));
        showToastMessage('Packaging type removed', 'success');
      }
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  const handleDelete = (id: number | undefined, index: number): void => {
    if (id !== undefined) {
      showConfirmationToast(
        'Are you sure you want to delete this item?',
        'Delete Confirmation',
        'delete',
        () => performDelete(id, index),
        'Delete',
        undefined,
        'Cancel',
      );
    } else {
      performDelete(id, index);
    }
  };

  // ---- Multiple delete selection ----
  const handleMultipleDeleteSelection = (
    id: number | undefined,
    index: number,
  ) => {
    setSelectedRecords((prev) => {
      const dataExists = prev.some((record) =>
        id !== undefined ? record.id === id : record.index === index,
      );
      return dataExists
        ? prev.filter((record) =>
          id !== undefined ? record.id !== id : record.index !== index,
        )
        : [...prev, { id, index }];
    });
  };

  const handleMultipleDelete = async (): Promise<void> => {
    try {
      const dataWithId = selectedRecords.filter(
        (record) => record.id !== undefined,
      );
      const dataWithIndex = selectedRecords.map((record) => record.index);
      if (dataWithId.length > 0) {
        const payload = dataWithId.map((record) => record.id);
        await Axios.delete(ApiEndpoints.DELETE_PACKAGE_TYPE, {
          data: { ids: payload },
        });
      }
      setPackagingTypes((prev) =>
        prev.filter((_, index) => !dataWithIndex.includes(index)),
      );
      setSelectedRecords([]);
      showToastMessage('Selected packaging types removed', 'success');
    } catch (error) {
      showToastMessage(errorMessage.DELETE_FAILED, 'error');
    }
  };

  // ---- Save ----
  const handleSubmit = async (
    event: React.FormEvent,
    nextPage: boolean,
  ): Promise<void> => {
    event.preventDefault();
    setErrors({});

    // Validate all rows
    const isValid = validateAllPackages();
    if (!isValid) {
      showToastMessage('Please fix all errors before saving.', 'error');
      return;
    }

    const isDataModified = isModified(initialList, packagingTypes);
    if (!isDataModified) {
      if (nextPage) {
        updateOnboardingStep(onboardingStepId.PACKAGE_TYPE);
        if (fromSettings) {
          navigate(FrontendRoutes.STORAGE_COST, { state: { fromSettings } });
        } else {
          navigate(FrontendRoutes.STORAGE_COST);
        }
      }
      return;
    }

    // Count updates
    let updatedCount = 0;
    for (const item of packagingTypes) {
      if (item.id !== undefined) {
        const initial = initialList.find((init) => init.id === item.id);
        if (initial) {
          if (
            initial.packageType !== item.packageType ||
            initial.price !== item.price ||
            initial.feeTypeId !== item.feeTypeId ||
            initial.packageSizeId !== item.packageSizeId ||
            initial.availableFlag !== item.availableFlag
          ) {
            updatedCount++;
          }
        }
      }
    }

    const payload: any[] = [];
    const restoreIds: number[] = [];
    let newItemsCount = 0;

    for (const types of packagingTypes) {
      const isNew = (types as any)._tempId !== undefined && !types.id;
      let finalId = types.id;

      if (isNew) {
        const deletedMatch = deletedPackageTypes.find(
          (d) =>
            d.packageType === types.packageType &&
            d.packageSizeId === types.packageSizeId
        );
        if (deletedMatch && deletedMatch.id) {
          restoreIds.push(deletedMatch.id);
          finalId = deletedMatch.id;
        } else {
          finalId = undefined;
          newItemsCount++;
        }
      }

      payload.push({
        ...(finalId !== undefined && { id: finalId }),
        price: Number(types.price),
        feeTypeId: types.feeTypeId,
        packageType: types.packageType.trim(),
        availableFlag: types.availableFlag ? 1 : 0,
        packageSizeId: types.packageSizeId,
      });
    }

    if (payload.length === 0 && restoreIds.length === 0) {
      showToastMessage(errorMessage.PRODUCT_LIST_EMPTY, 'error');
      return;
    }

    try {
      let restoredCount = 0;
      if (restoreIds.length > 0) {
        await Axios.post(ApiEndpoints.RESTORE_PACKAGE_TYPE, { ids: restoreIds });
        restoredCount = restoreIds.length;
        setDeletedPackageTypes((prev) =>
          prev.filter((d) => !restoreIds.includes(d.id!))
        );
        await fetchAllPackageTypes(currentPage, searchKey, itemsPerPage);
      }

      if (payload.length > 0) {
        await Axios.post(ApiEndpoints.CREATE_PACKAGE_TYPE, payload);
        await fetchAllPackageTypes(currentPage, searchKey, itemsPerPage);
      }

      // Build success message
      const totalAdded = restoredCount + newItemsCount;
      let message = '';
      if (totalAdded > 0 && updatedCount > 0) {
        const addedWord = totalAdded === 1 ? 'package type' : 'package types';
        const updatedWord = updatedCount === 1 ? 'package type' : 'package types';
        message = `${totalAdded} ${addedWord} added and ${updatedCount} ${updatedWord} updated successfully.`;
      } else if (totalAdded > 0) {
        if (totalAdded === 1) {
          message = 'Package type created successfully.';
        } else {
          message = `${totalAdded} package types created successfully.`;
        }
      } else if (updatedCount > 0) {
        if (updatedCount === 1) {
          message = 'Package type updated successfully.';
        } else {
          message = `${updatedCount} package types updated successfully.`;
        }
      } else {
        message = responseTexts.PACKAGE_TYPE_UPSERT_SUCCESS || 'Package types saved successfully.';
      }
      showToastMessage(message, 'success');

      updateOnboardingStep(onboardingStepId.PACKAGE_TYPE);
      setHasUnsavedChanges(false);

      if (nextPage) {
        if (fromSettings) {
          navigate(FrontendRoutes.STORAGE_COST, { state: { fromSettings } });
        } else {
          navigate(FrontendRoutes.STORAGE_COST);
        }
      }
    } catch (error) {
      showToastMessage(getErrorMessage(error), 'error');
    }
  };

  // ---- Next handler ----
  const handleNext = () => {
    updateOnboardingStep(onboardingStepId.PACKAGE_TYPE);
    if (fromSettings) {
      navigate(FrontendRoutes.STORAGE_COST, { state: { fromSettings } });
    } else {
      navigate(FrontendRoutes.STORAGE_COST);
    }
  };

  // ---- Search ----
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchInput(value);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      if (value.length >= 3) {
        setSearchKey(value);
      } else {
        setSearchKey('');
      }
    }, 500);
  };

  const getUnitDisplay = (feeTypeId: number, _defaultFlag: number): string => {
    const feeType = feeTypeOptions.find((fee) => fee.id === feeTypeId);
    if (feeType?.feeType === 'Flat Fee') return '-';
    return feeType?.unit || 'lbs';
  };

  // ---- Render ----
  const renderContent = (
    <>
      <div>
        <Breadcrumbs
          title="Configure Settings"
          activeMainNav="Products"
          activeStep={3}
          subNavSteps={productSubsteps}
          fromSettings={fromSettings ? true : false}
        />
      </div>

      {isFeetypeSheetOpen && (
        <PackageFeeTypeBottomSheet
          onClose={() => setisFeetypeSheetOpen(false)}
          onSelect={handleSelectFeeTypeForRow}
          usedFeeTypeIds={usedFeeTypeIds}
        />
      )}

      <div className="mt-3 rounded-lg">
        <div className="flex justify-between items-center mb-6">
          <h1 className="font-semibold text-xl px-5 mt-3">Packaging Type</h1>
          <div className="flex items-center gap-2">
            <div className="relative">
              <input
                type="text"
                placeholder="Search..."
                onChange={handleSearchChange}
                value={searchInput}
                className="border p-2 pl-3 pr-10 w-80 accent-primary focus:outline-primary border-gray-300 rounded"
              />
              <FontAwesomeIcon
                icon={faSearch}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
              />
            </div>
          </div>
        </div>

        <div className="max-w-full">
          <div
            className="flex-1 overflow-y-auto mb-6 relative shadow-lg h-[calc(100vh-480px)]"
            id="packaging-table-scroll-area"
          >
            <table className="w-full">
              <thead className="bg-gray-100">
                <tr className="border-b items-center">
                  <th className="px-4 py-2 align-middle">
                    <div className="flex items-center gap-3 font-semibold">
                      <span>Title</span>
                      <img
                        src={Assets.SORT_ICON}
                        alt="Sort"
                        className="h-3 w-3"
                      />
                    </div>
                  </th>
                  <th className="px-4 py-2 text-center font-semibold whitespace-nowrap align-middle">
                    Size in lbs
                  </th>
                  <th className="px-4 py-2 text-center font-semibold whitespace-nowrap align-middle">
                    Fee Type
                  </th>
                  <th className="px-4 py-2 text-center font-semibold whitespace-nowrap align-middle">
                    Price
                  </th>
                  <th className="px-4 py-2 text-center font-semibold whitespace-nowrap align-middle flex items-center justify-center gap-2">
                    Available
                    <input
                      checked={selectAll}
                      onChange={handleSelectAllChange}
                      type="checkbox"
                      className="form-checkbox h-4 w-4 accent-primary focus:ring-primary text-primary rounded-full"
                    />
                  </th>
                  <th className="px-4 py-2 text-center font-semibold whitespace-nowrap align-middle"></th>
                </tr>
              </thead>
              <tbody>
                {packagingTypes.length > 0 ? (
                  packagingTypes.map((item, index) => {
                    const isDefault = item.defaultFlag === 1;
                    const isAddingSize = addingSizeForRowIndex === index;
                    const sizeLabel = sizeData.find(s => s.id === item.packageSizeId)?.packageSize || '-';
                    const feeLabel = feeTypeOptions.find(f => f.id === item.feeTypeId)?.feeType || '-';
                    const unitLabel = getUnitDisplay(item.feeTypeId, item.defaultFlag);

                    return (
                      <tr
                        key={item.id ?? (item as any)._tempId ?? index}
                        className={`
                          ${onDeletion ? 'bg-[#F6F5F5]' : ''}
                          bg-[#FFFEFA]
                          align-middle
                        `}
                      >
                        {/* Package Type */}
                        <td className="px-2 py-2 min-w-[150px] align-middle">
                          {isDefault ? (
                            <span className="font-medium text-gray-800">{item.packageType}</span>
                          ) : (
                            <>
                              <input
                                name="packageType"
                                type="text"
                                value={item.packageType}
                                onChange={(e) =>
                                  handleRowFieldChange(
                                    index,
                                    'packageType',
                                    e.target.value,
                                  )
                                }
                                disabled={onDeletion}
                                className={`border-b-2 focus:outline-none w-full max-w-[120px] px-1 focus:border-primary ${onDeletion
                                  ? 'border-transparent cursor-not-allowed opacity-70'
                                  : 'border-[#555F71]'
                                  } ${errors[`packageType_${index}`] ? 'border-red-500' : ''}`}
                              />
                              {errors[`packageType_${index}`] && (
                                <p className="text-red-500 text-xs mt-1">
                                  {errors[`packageType_${index}`]}
                                </p>
                              )}
                            </>
                          )}
                        </td>

                        {/* Size */}
                        <td className="px-4 py-2 text-center align-middle relative">
                          {isDefault ? (
                            <span>{sizeLabel}</span>
                          ) : (
                            <div className="flex items-center justify-center">
                              <div className="relative w-full max-w-xs">
                                <select
                                  data-testid="packageSizeId"
                                  name="packageSizeId"
                                  onChange={(e) => {
                                    if (e.target.value === 'add-size') {
                                      setAddingSizeForRowIndex(index);
                                      return;
                                    }
                                    handleRowFieldChange(
                                      index,
                                      'packageSizeId',
                                      Number(e.target.value),
                                    );
                                  }}
                                  value={item.packageSizeId}
                                  disabled={onDeletion || isAddingSize}
                                  className={`w-full p-2 pr-8 border border-gray-300 rounded flex justify-between items-center bg-white outline outline-1 outline-gray-300 focus:outline-2 focus:outline-primary cursor-pointer appearance-none h-[42px] ${onDeletion || isAddingSize
                                    ? 'opacity-70 cursor-not-allowed'
                                    : ''
                                    }`}
                                >
                                  <option value={0} disabled>
                                    Select Size
                                  </option>
                                  {sizeData.map((size) => (
                                    <option key={size.id} value={size.id}>
                                      {size.packageSize}
                                    </option>
                                  ))}
                                  <option
                                    value="add-size"
                                    className="text-primary font-bold"
                                  >
                                    Add
                                  </option>
                                </select>
                                <div className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2">
                                  <svg width="15" height="8" viewBox="0 0 15 8" fill="none">
                                    <path
                                      d="M2 1.5L7.5 6.5L13 1.5"
                                      stroke="#A09CAB"
                                      strokeWidth="2.5"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                </div>
                                {isAddingSize && (
                                  <div className="absolute top-full left-0 mt-1 min-w-[160px] max-w-[200px] bg-white border border-gray-300 rounded-md p-2 shadow-lg z-50">
                                    <input
                                      type="number"
                                      step="0.01"
                                      min="0"
                                      placeholder="New size"
                                      value={newsizeData}
                                      onChange={(e) =>
                                        setNewsizeData(e.target.value)
                                      }
                                      className="w-3/5 px-1 py-1 border rounded-md text-sm text-gray-700 outline-none"
                                    />
                                    <FontAwesomeIcon
                                      onClick={() => handleAddNewSize(index)}
                                      icon={faSquareCheck}
                                      className="mx-1 text-primary text-xl cursor-pointer"
                                    />
                                    <FontAwesomeIcon
                                      onClick={() => {
                                        setAddingSizeForRowIndex(null);
                                        setNewsizeData('');
                                      }}
                                      icon={faSquareXmark}
                                      className="text-gray-500 text-xl cursor-pointer"
                                    />
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                          {!isDefault && errors[`packageSizeId_${index}`] && (
                            <p className="text-red-500 text-xs mt-1">
                              {errors[`packageSizeId_${index}`]}
                            </p>
                          )}
                        </td>

                        {/* Fee Type */}
                        <td className="px-4 py-2 text-center align-middle">
                          {isDefault ? (
                            <span>{feeLabel}</span>
                          ) : (
                            <button
                              type="button"
                              className={`w-full p-2 border bg-white border-gray-300 rounded flex justify-between items-center cursor-pointer overflow-hidden ${onDeletion
                                ? 'opacity-70 cursor-not-allowed'
                                : ''
                                }`}
                              onClick={() => {
                                if (!onDeletion) {
                                  setSelectedFeeTypeRowIndex(index);
                                  setisFeetypeSheetOpen(true);
                                }
                              }}
                              disabled={onDeletion}
                            >
                              <span className="truncate max-w-[100%] overflow-hidden">
                                {feeTypeOptions.find(
                                  (fee) => fee.id === item.feeTypeId,
                                )?.feeType || '-'}
                              </span>
                              <span>
                                <svg width="15" height="8" viewBox="0 0 15 8" fill="none">
                                  <path
                                    d="M2 1.5L7.5 6.5L13 1.5"
                                    stroke="#A09CAB"
                                    strokeWidth="2.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              </span>
                            </button>
                          )}
                          {!isDefault && errors[`feeTypeId_${index}`] && (
                            <p className="text-red-500 text-xs mt-1">
                              {errors[`feeTypeId_${index}`]}
                            </p>
                          )}
                        </td>

                        {/* Price */}
                        <td className="px-2 py-2 text-center min-w-[160px] align-middle">
                          {isDefault ? (
                            <span>${Number(item.price).toFixed(2)} / {unitLabel}</span>
                          ) : (
                            <div className="flex items-center justify-center">
                              <div className="flex items-center border rounded-md bg-white text-gray-600 focus-within:outline-primary">
                                <span className="px-2 text-gray-600">$</span>
                                <input
                                  name="price"
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={item.price}
                                  onChange={(e) =>
                                    handleRowFieldChange(
                                      index,
                                      'price',
                                      e.target.value,
                                    )
                                  }
                                  disabled={onDeletion}
                                  className={`w-16 p-2 focus:outline-none text-gray-600 ${onDeletion
                                    ? 'opacity-70 cursor-not-allowed'
                                    : ''
                                    }`}
                                />
                              </div>
                              <span className="ml-1 text-gray-600">
                                / {getUnitDisplay(item.feeTypeId, item.defaultFlag)}
                              </span>
                            </div>
                          )}
                          {!isDefault && errors[`price_${index}`] && (
                            <p className="text-red-500 text-xs">
                              {errors[`price_${index}`]}
                            </p>
                          )}
                        </td>

                        {/* Available */}
                        <td className="px-2 py-2 text-center align-middle">
                          {isDefault ? (
                            <span>
                              <FontAwesomeIcon icon={faCheck} className="text-green-600" />
                            </span>
                          ) : (
                            <input
                              name="availableFlag"
                              type="checkbox"
                              disabled={!!onDeletion}
                              checked={item.availableFlag}
                              onChange={(e) =>
                                handleAvailableFlagChange(index, e.target.checked)
                              }
                              className={`h-4 w-4 accent-primary focus:ring-primary border-gray-300 rounded ${onDeletion
                                ? 'opacity-50 cursor-not-allowed'
                                : ''
                                }`}
                            />
                          )}
                        </td>

                        {/* Actions */}
                        <td className="px-2 py-2 text-right align-middle">
                          {!isDefault && (
                            <>
                              {onDeletion && (
                                <input
                                  type="checkbox"
                                  name="deletion"
                                  checked={selectedRecords.some((record) =>
                                    item.id !== undefined
                                      ? record.id === item.id
                                      : record.index === index,
                                  )}
                                  onChange={() =>
                                    handleMultipleDeleteSelection(item.id, index)
                                  }
                                  className="h-4 w-4 accent-primary focus:ring-primary border-gray-300 rounded mr-2"
                                />
                              )}
                              <button
                                type="button"
                                onClick={() => handleDelete(item.id, index)}
                                disabled={onDeletion}
                                className={onDeletion ? 'opacity-50 cursor-not-allowed' : ''}
                              >
                                <FontAwesomeIcon icon={faTrashCan} />
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={6} className="text-center p-4 align-middle">
                      No data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div>
          <Pagination
            totalPages={totalPages}
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={setItemsPerPage}
          />
        </div>
      </div>

      <div className="sticky bottom-0 bg-white mt-3">
        {onDeletion ? (
          <div className="flex justify-between gap-4">
            <button
              type="button"
              onClick={() => setOnDeletion(false)}
              className="w-[50%] px-6 py-1 border border-primary rounded-sm text-sm font-medium text-primary bg-white hover:bg-primary hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            >
              Cancel
            </button>
            <button
              type="submit"
              onClick={() =>
                showConfirmationToast(
                  toastMessage.delteConfirmation,
                  'Remove',
                  'delete',
                  handleMultipleDelete,
                  'Remove all',
                )
              }
              className="w-[50%] px-6 py-1 border border-red-500 rounded-sm text-sm font-medium text-red-500 bg-white hover:text-red-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 flex items-center justify-center gap-2"
            >
              <FontAwesomeIcon icon={faTrashCan} />
              <span>Remove All</span>
            </button>
          </div>
        ) : (
          <div className="w-full mt-6 py-4 border-t border-gray-100">
            <div className="flex flex-col 2xl:flex-row 2xl:items-center 2xl:justify-end gap-4 w-full">
              <button
                type="button"
                onClick={handleAddMore}
                className="w-full 2xl:w-[150px] h-10 px-6 flex items-center justify-center space-x-2 border border-transparent rounded-md text-sm font-medium text-white bg-primary hover:bg-dark-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors duration-200"
              >
                <span>+ Add More</span>
              </button>

              <div className="w-full 2xl:w-auto flex items-center justify-between 2xl:justify-end gap-4">
                <button
                  type="button"
                  onClick={(e) => handleSubmit(e, false)}
                  disabled={!hasUnsavedChanges}
                  className={`w-1/2 2xl:w-[130px] h-10 px-6 border rounded-md text-sm font-medium flex items-center justify-center transition-all duration-200 ${hasUnsavedChanges
                    ? 'bg-white text-primary border-primary hover:bg-primary hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary'
                    : 'bg-white text-[#00483480] cursor-not-allowed border-[#00483480] pointer-events-auto'
                    }`}
                >
                  Save
                </button>

                <button
                  type="button"
                  onClick={handleNext}
                  className="w-1/2 2xl:w-[150px] h-10 px-6 border border-transparent rounded-md text-sm font-medium text-white bg-primary hover:bg-dark-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary flex items-center justify-center space-x-2 transition-colors duration-200"
                >
                  <span>Next</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );

  return (
    <>
      {!fromSettings ? (
        <div className="w-screen h-screen overflow-auto flex justify-center items-start">
          <div className="w-11/12 p-4">
            <ButcherHeader isOnBoarding={true} />
            {renderContent}
          </div>
        </div>
      ) : (
        <OnBoardSettings renderContent={renderContent} speciesSetup={true} />
      )}
    </>
  );
};